// src/pages/health/components/healthCareModal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { XMarkIcon } from "@heroicons/react/24/solid";

/**
 * 범용 헬스 기록 모달
 * - variant: 'bp' | 'weight' (추후 'glucose' 확장 가능)
 */
export default function HealthCareModal({
    open,
    mode = "create",            // 'create' | 'edit'
    variant = "bp",              // 'bp' | 'weight'
    date = new Date(),
    initial = {},
    confirmLabel = "변경 내용 저장",
    onClose,
    onSubmit,
    busy = false,
}) {
    // 필드 상태: bp 전용(sys/dia), weight 전용(weight)
    const [sys, setSys] = useState(initial.sys ?? "");
    const [dia, setDia] = useState(initial.dia ?? "");
    const [note, setNote] = useState(initial.note ?? "");
    const [weight, setWeight] = useState(initial.weight ?? "");
    const [bodyFat, setBodyFat] = useState(
        initial.body_fat ??
        initial.body_fat_mass ??
        initial.bodyFatMass ??
        initial.fat_mass ??
        initial.bodyFat ??
        "",
    );
    const [fasting, setFasting] = useState(initial.glucose ?? "");

    const firstInputRef = useRef(null);

    // open 시 focus & 스크롤 잠금
    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            const t = setTimeout(() => firstInputRef.current?.focus(), 10);
            return () => {
                document.body.style.overflow = prev;
                clearTimeout(t);
            };
        }
    }, [open]);

    // title
    const title = mode === "edit" ? "기록 수정" : "기록 추가";

    // date 문자열/Date 모두 허용
    const ymd = useMemo(() => {
        const d = typeof date === "string" ? new Date(date) : date;
        if (Number.isNaN(d?.getTime?.())) return "";
        return format(d, "yyyy-MM-dd");
    }, [date]);

    // 저장 시 전송할 로컬 기준 datetime 문자열
    const buildDt = () => {
        const base = typeof date === "string" ? new Date(date) : new Date(date);
        const now = new Date();
        base.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
        const pad = (n) => String(n).padStart(2, "0");
        return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}T${pad(
            base.getHours()
        )}:${pad(base.getMinutes())}:${pad(base.getSeconds())}`;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        if (variant === "bp") {
            const s = Number(sys);
            const d = Number(dia);
            if (!s || !d) {
                window.alert("수축/이완 혈압을 입력해주세요.");
                return;
            }
            await onSubmit?.({ sys: s, dia: d, note: note || "", dt: buildDt() });
        } else if (variant === "weight") {
            const w = Number(weight);
            if (!w) {
                window.alert("체중을 입력해주세요.");
                return;
            }
            await onSubmit?.({ weight: w, note: note || "", dt: buildDt() });
        } else if (variant === "bodyFat") {
            const f = Number(bodyFat);
            if (!f) {
                window.alert("체지방량을 입력해주세요.");
                return;
            }
            await onSubmit?.({ body_fat: f, note: note || "", dt: buildDt() });
        }

        else if (variant === "glucose") {
            const f = Number(fasting);
            if (!f) { alert("혈당(공복)을 입력해주세요."); return; }
            await onSubmit?.({ fasting: f, note: note || "", dt: buildDt() });
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Dimmed background */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
            {/* Modal card */}
            <div className="relative z-10 w-[min(680px,92vw)] max-w-[680px] rounded-2xl bg-white shadow-xl">
                {/* Header */}
                <div className="px-6 pt-5 pb-2 flex items-center justify-center">
                    <div className="absolute right-3 top-3">
                        <button
                            type="button"
                            className="p-2 rounded-full hover:bg-gray-100 active:scale-95"
                            onClick={onClose}
                            disabled={busy}
                            aria-label="닫기"
                        >
                            <XMarkIcon className="w-6 h-6 text-gray-400" />
                        </button>
                    </div>
                    <h2 className="text-xl md:text-2xl font-extrabold">{title}</h2>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">

                    <div className="space-y-2">
                        <label className="text-sm md:text-base text-gray-600">기록 일자</label>
                        <div className="h-12 rounded-xl bg-gray-100 text-gray-600 flex items-center px-4 font-medium">
                            {ymd || "-"}
                        </div>
                    </div>

                    {/* variant별 입력 */}
                    {variant === "bp" && (
                        <div className="space-y-2">
                            <label className="text-sm md:text-base text-gray-600">수축 / 이완 혈압</label>
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    ref={firstInputRef}
                                    type="number"
                                    inputMode="numeric"
                                    placeholder="수축(예: 130)"
                                    min={50}
                                    max={300}
                                    value={sys}
                                    onChange={(e) => setSys(e.target.value)}
                                    className="h-12 rounded-xl border border-gray-300 px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#5F9BFF]"
                                    required
                                />
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    placeholder="이완(예: 85)"
                                    min={30}
                                    max={200}
                                    value={dia}
                                    onChange={(e) => setDia(e.target.value)}
                                    className="h-12 rounded-xl border border-gray-300 px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#5F9BFF]"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {variant === "weight" && (
                        <div className="space-y-2">
                            <label className="text-sm md:text-base text-gray-600">체중</label>
                            <div className="grid grid-cols-1 gap-3">
                                <input
                                    ref={firstInputRef}
                                    type="number"
                                    step="0.1"
                                    inputMode="decimal"
                                    placeholder="예: 72.4"
                                    min={1}
                                    max={500}
                                    value={weight}
                                    onChange={(e) => setWeight(e.target.value)}
                                    className="h-12 rounded-xl border border-gray-300 px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#5F9BFF]"
                                    required
                                />
                            </div>
                        </div>
                    )}


                    {variant === "bodyFat" && (
                        <div className="space-y-2">
                            <label className="text-sm md:text-base text-gray-600">체지방량</label>
                            <div className="grid grid-cols-1 gap-3">
                                <input
                                    ref={firstInputRef}
                                    type="number"
                                    step="0.1"
                                    inputMode="decimal"
                                    placeholder="예: 18.2"
                                    min={1}
                                    max={300}
                                    value={bodyFat}
                                    onChange={(e) => setBodyFat(e.target.value)}
                                    className="h-12 rounded-xl border border-gray-300 px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#5F9BFF]"
                                    required
                                />
                            </div>
                        </div>
                    )}


                    {variant === "glucose" && (
                        <div className="space-y-2">
                            <label className="text-sm md:text-base text-gray-600">공복 혈당</label>
                            <input
                                ref={firstInputRef}
                                type="number"
                                inputMode="numeric"
                                placeholder="예: 95"
                                min={20}
                                max={500}
                                value={fasting}
                                onChange={(e) => setFasting(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-[#5F9BFF]"
                                required
                            />
                        </div>
                    )}


                    {/* 메모 */}
                    <div className="space-y-2">
                        <label className="text-sm md:text-base text-gray-600">메모 사항 (선택)</label>
                        <textarea
                            rows={4}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-[#5F9BFF]"
                        />
                    </div>

                    {/* Action */}
                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full h-12 md:h-12 rounded-xl bg-[#5F9BFF] text-white font-semibold active:scale-[0.99] disabled:opacity-60"
                        >
                            {busy ? "저장 중..." : confirmLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
